import { TOnboardingAnswers } from "./OnboardingRules";

/**
 * The onboarding page's markup: one section per screen, written by Twig. This owns every
 * selector; it reads the boxes into the answers and shows one screen or one error at a time.
 */
export default class OnboardingForm {
  private readonly steps: JQuery<HTMLElement>;
  private readonly backButton: JQuery<HTMLElement>;
  private readonly messages: JQuery<HTMLElement>;
  private readonly kidNameTemplate: JQuery<HTMLElement>;
  private readonly kidNameList: JQuery<HTMLElement>;
  private readonly kidLabels: JQuery<HTMLElement>;
  /** The login link as Twig wrote it; the email of the moment is added to it, never stacked */
  private readonly loginHref: string;
  /** The buttons a server call greyed; the ones already grey (a screen waiting for its answer) stay so */
  private busyButtons: JQuery<HTMLElement> | null = null;

  constructor(
    private readonly root: JQuery<HTMLElement>,
    /** Each screen opens at the top of the page */
    private readonly scrollToTop: () => void = () =>
      window.scrollTo({ top: 0 }),
  ) {
    this.steps = root.find(".js-ob-step");
    this.backButton = root.find(".js-ob-back");
    this.messages = root.find(".ob-messages");
    this.kidNameTemplate = root.find(".ob-kidname.template");
    this.kidNameList = root.find(".js-ob-kid-names");
    this.kidLabels = root.find(".ob-kid-label-template");
    this.loginHref = root.find(".js-ob-email-login").attr("href") ?? "";
  }

  public Show(step: string, canGoBack: boolean): void {
    this.steps.addClass("hide").removeAttr("inert");
    const shown = this.steps
      .filter(`[data-step="${step}"]`)
      .removeClass("hide");
    // The consent is a card over the password screen, which shows through but takes nothing
    if (step === "terms") {
      this.steps
        .filter('[data-step="password"]')
        .removeClass("hide")
        .attr("inert", "");
    }
    this.backButton.toggleClass("hide", !canGoBack);
    shown.find(".js-ob-error, .js-ob-email-login").addClass("hide");
    // The caret goes to the screen's box; a screen of choices (the avatars) gives it its picked
    // tile or its first one, so a keyboard does not start again from the top of the page
    const picked = shown.find(".js-ob-avatar.is-selected");
    (picked.length > 0 ? picked : shown.find("input:visible, .js-ob-avatar"))
      .first()
      .trigger("focus");
    this.scrollToTop();
  }

  /** The message for an error key of the page, or the text the server sent. */
  public ShowError(step: string, keyOrText: string, fromServer = false): void {
    const text = fromServer
      ? keyOrText
      : (this.messages.attr(`data-error-${keyOrText}`) ??
        this.messages.attr("data-error-generic") ??
        "");
    this.steps
      .filter(`[data-step="${step}"]`)
      .find(".js-ob-error")
      .text(text)
      .removeClass("hide");
  }

  public HideError(step: string): void {
    this.steps
      .filter(`[data-step="${step}"]`)
      .find(".js-ob-error")
      .addClass("hide");
  }

  public ShowGenericError(step: string): void {
    this.ShowError(step, "generic");
  }

  /** The email has an account: say so and show the way to the login, with the email along. */
  public OfferLogin(email: string): void {
    this.ShowError("email", "email-taken");
    const separator = this.loginHref.includes("?") ? "&" : "?";
    this.root
      .find(".js-ob-email-login")
      .attr(
        "href",
        `${this.loginHref}${separator}email=${encodeURIComponent(email)}`,
      )
      .removeClass("hide");
  }

  /** The screens whose button waits for a complete answer, as the prototype does; the last screen of a path has the sending one. */
  public EnableNext(step: string, enabled: boolean): void {
    this.steps
      .filter(`[data-step="${step}"]`)
      .find(".js-ob-next, .js-ob-submit")
      .prop("disabled", !enabled);
  }

  /** Puts restored answers back into the boxes: the mirror of ReadInto. */
  public Fill(answers: TOnboardingAnswers): void {
    this.root.find(".js-ob-email").val(answers.email);
    this.root.find(".js-ob-dob-day").val(answers.birthDate.day);
    this.root.find(".js-ob-dob-month").val(answers.birthDate.month);
    this.root.find(".js-ob-dob-year").val(answers.birthDate.year);
    this.root.find(".js-ob-consent").prop("checked", answers.consent);
    this.root.find(".js-ob-password").val(answers.password);
    this.root.find(".js-ob-password-confirm").val(answers.passwordConfirm);
    this.root.find(".js-ob-parent-name").val(answers.parentName);
    this.SelectKidCount(answers.kidCount);
    this.RenderKidNames(answers.kidCount, answers.kidNames);
    this.SelectAvatar(answers.avatar);
    this.root.find(".js-ob-child-name").val(answers.childName);
  }

  /** Greys every button while the server answers; only the ones it greyed come back after. */
  public SetBusy(busy: boolean): void {
    if (busy) {
      this.busyButtons = this.root
        .find(".js-ob-next, .js-ob-submit, .js-ob-back")
        .filter(":enabled")
        .prop("disabled", true);
      return;
    }
    this.busyButtons?.prop("disabled", false);
    this.busyButtons = null;
  }

  /**
   * Copies what the boxes hold into the answers. The email box is read only when the page asked
   * for it: otherwise the email came with the page and the box stays empty. The PIN is the
   * flow's own: it is kept only once typed twice.
   */
  public ReadInto(answers: TOnboardingAnswers, emailAsked: boolean): void {
    if (emailAsked) {
      answers.email = String(this.root.find(".js-ob-email").val() ?? "");
    }
    answers.birthDate = {
      day: String(this.root.find(".js-ob-dob-day").val() ?? ""),
      month: String(this.root.find(".js-ob-dob-month").val() ?? ""),
      year: String(this.root.find(".js-ob-dob-year").val() ?? ""),
    };
    answers.consent = this.root.find(".js-ob-consent").is(":checked");
    answers.password = String(this.root.find(".js-ob-password").val() ?? "");
    answers.passwordConfirm = String(
      this.root.find(".js-ob-password-confirm").val() ?? "",
    );
    answers.parentName = String(
      this.root.find(".js-ob-parent-name").val() ?? "",
    );
    answers.kidNames = this.kidNameList
      .find(".js-ob-kid-name")
      .map((_i, input) => String($(input).val() ?? ""))
      .get();
    answers.childName = String(this.root.find(".js-ob-child-name").val() ?? "");
  }

  /** Whether the page offers a tile for this avatar: a restored answer may name one it does not. */
  public HasAvatar(avatar: string): boolean {
    return (
      this.root
        .find(".js-ob-avatar")
        .filter((_i, tile) => $(tile).attr("data-avatar") === avatar).length > 0
    );
  }

  /** The avatar tile picked, or none; the button follows through the flow's gate. */
  public SelectAvatar(avatar: string | null): void {
    this.root.find(".js-ob-avatar").each((_i, tile) => {
      const selected =
        avatar !== null && $(tile).attr("data-avatar") === avatar;
      $(tile)
        .toggleClass("is-selected", selected)
        .attr("aria-pressed", selected ? "true" : "false");
    });
  }

  public SelectKidCount(count: number): void {
    this.root.find(".js-ob-kid-count").each((_i, button) => {
      const selected = Number($(button).attr("data-count")) === count;
      $(button)
        .toggleClass("is-selected", selected)
        .attr("aria-checked", selected ? "true" : "false");
    });
    this.steps
      .filter('[data-step="kidCount"]')
      .find(".js-ob-next")
      .prop("disabled", count === 0);
  }

  /** One box per child, keeping what was typed for the children that stay. */
  public RenderKidNames(count: number, names: string[]): void {
    const placeholders = (this.kidLabels.attr("data-placeholders") ?? "").split(
      "|",
    );
    this.kidNameList.empty();
    for (let i = 0; i < count; i++) {
      const row = this.kidNameTemplate.clone().removeClass("template hide");
      const label =
        count === 1
          ? (this.kidLabels.attr("data-one") ?? "")
          : (this.kidLabels.attr("data-many") ?? "").replace(
              "%n%",
              String(i + 1),
            );
      row.find("label").text(label).attr("for", `ob-kid-name-${i}`);
      row
        .find("input")
        .attr("id", `ob-kid-name-${i}`)
        .attr("placeholder", placeholders[i] ?? "")
        .val(names[i] ?? "");
      this.kidNameList.append(row);
    }
    this.root.find(".js-ob-kid-names-one").toggleClass("hide", count !== 1);
    this.root.find(".js-ob-kid-names-many").toggleClass("hide", count === 1);
  }

  /**
   * The four boxes mirror the hidden input: a dot per digit, the next box outlined. The button
   * waits for the PIN to be confirmed.
   */
  public RenderPin(pin: string, focused: boolean, confirmed: boolean): void {
    this.root.find(".ob-pin-box").each((i, box) => {
      $(box)
        .text(pin[i] ? "•" : "")
        .toggleClass("is-active", focused && i === Math.min(pin.length, 3));
    });
    this.steps
      .filter('[data-step="pin"]')
      .find(".js-ob-submit")
      .prop("disabled", !confirmed);
  }

  /** The PIN screen asks once, then asks again: the title and the text say which time it is. */
  public ShowPinPhase(phase: "create" | "confirm"): void {
    this.root.find(".js-ob-pin-title, .js-ob-pin-text").each((_i, element) => {
      $(element).text($(element).attr(`data-${phase}`) ?? "");
    });
  }

  public TogglePasswordVisibility(): void {
    const inputs = this.root.find(".js-ob-password, .js-ob-password-confirm");
    const toggle = this.root.find(".js-ob-pwd-toggle");
    const show = inputs.first().attr("type") === "password";
    inputs.attr("type", show ? "text" : "password");
    toggle
      .text(toggle.attr(show ? "data-hide" : "data-show") ?? "")
      .attr("aria-pressed", show ? "true" : "false");
  }

  public get pinInput(): JQuery<HTMLElement> {
    return this.root.find(".js-ob-pin");
  }
}
